import { Box } from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { updateCustomListItem } from 'entities/CustomList';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useDebouncedCallback } from 'shared/lib/hooks/useDebouncedCallback';
import { OrderItemQuantityPicker } from 'shared/ui/OrderItemQuantityPicker';

const MIN_LOADING_MS = 500;

interface CustomListItemQuantityCellProps {
    listId: string;
    itemId: string;
    value: number;
    disabled?: boolean;
}

export const CustomListItemQuantityCell = memo(
    ({
        listId,
        itemId,
        value,
        disabled = false,
    }: CustomListItemQuantityCellProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const [quantity, setQuantity] = useState(value);
        const [isUpdating, setIsUpdating] = useState(false);

        useEffect(() => {
            setQuantity(value);
        }, [value]);

        const debouncedUpdate = useDebouncedCallback(
            useCallback(
                async (nextQty: number) => {
                    if (!listId || nextQty < 1) {
                        return;
                    }

                    const startedAt = Date.now();
                    setIsUpdating(true);

                    try {
                        await dispatch(
                            updateCustomListItem({
                                listId,
                                itemId,
                                targetQuantity: nextQty,
                            }),
                        ).unwrap();
                    } catch {
                        toast.error(t('Error updating item quantity'));
                        setQuantity(value);
                    } finally {
                        const remainingMs = Math.max(
                            0,
                            MIN_LOADING_MS - (Date.now() - startedAt),
                        );
                        if (remainingMs > 0) {
                            await new Promise((resolve) => {
                                setTimeout(resolve, remainingMs);
                            });
                        }
                        setIsUpdating(false);
                    }
                },
                [dispatch, itemId, listId, t, value],
            ),
        );

        const onQuantityChange = useCallback(
            (nextQty: number) => {
                setQuantity(nextQty);
                debouncedUpdate(nextQty);
            },
            [debouncedUpdate],
        );

        return (
            <Box data-testclass="custom-list-item-qty">
                <OrderItemQuantityPicker
                    value={quantity}
                    minQuantity={1}
                    disabled={disabled || isUpdating}
                    showLoading={isUpdating}
                    onQuantityChange={onQuantityChange}
                />
            </Box>
        );
    },
);

CustomListItemQuantityCell.displayName = 'CustomListItemQuantityCell';
